import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { formatDate } from '../../../utils/reparation';
import { capitalizeFirstLetter } from '../../../utils/utils';
import { fixerPhotoUrl } from '../../../utils/fixers';
import Box from '../../shared/atoms/Box';
import CourseSectionHeading from './CourseSectionHeading';
import BrandIcon from './BrandIcon';
import Calendar24 from '../../icons/Calendar24';
import { RJ, TITLE_FONT } from '../../rapport/rapportTokens';

// §5.4 — "Votre rendez-vous" : fixer avatar + name + confirmed date + address.
// Finished-state only; page-scoped brand charte (RJ + AronVF).
const Card = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 14px;
  background: ${RJ.bg};
  border: 1px solid ${RJ.border};
  border-radius: ${RJ.radiusLg};
  padding: 16px;
`;

const Avatar = styled.img`
  width: 56px;
  height: 56px;
  min-width: 56px;
  border-radius: 50%;
  object-fit: cover;
`;

const AvatarFallback = styled.div`
  width: 56px;
  height: 56px;
  min-width: 56px;
  border-radius: 50%;
  background: ${RJ.greenPastel};
`;

const Body = styled.div`
  flex: 1;
  min-width: 0;
`;

const Name = styled.div`
  font-family: ${TITLE_FONT};
  font-weight: 900;
  font-size: 16px;
  color: ${RJ.text};
`;

const Role = styled.div`
  font-size: 13px;
  color: ${RJ.textSecondary};
  margin-bottom: 8px;
`;

const InfoRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: ${RJ.text};
  margin-top: 6px;

  & svg {
    flex-shrink: 0;
  }
`;

const FinishedAppointmentCard = ({ course }) => {
  const { t } = useTranslation();
  const fixer = course && course.fixer;
  if (!fixer) return null;

  const hasPhoto = !!fixer.photo_id;
  const when = course.date
    ? `${capitalizeFirstLetter(
        formatDate(course.date, 'dddd DD/MM'),
      )} · ${formatDate(course.date, 'HH:mm')}`
    : null;

  return (
    <Box mb={9}>
      <CourseSectionHeading icon={<BrandIcon name="rendez-vous" size={28} />}>
        {t('course.appointment.heading')}
      </CourseSectionHeading>
      <Card data-testid="finished-appointment-card">
        {hasPhoto ? (
          <Avatar src={fixerPhotoUrl(fixer)} alt={fixer.prenom || ''} />
        ) : (
          <AvatarFallback aria-hidden="true" />
        )}
        <Body>
          <Name>{fixer.prenom}</Name>
          <Role>{t('course.appointment.role')}</Role>
          {when && (
            <InfoRow>
              <Calendar24 color={RJ.blue} />
              <span>{when}</span>
            </InfoRow>
          )}
          {course.position && (
            <InfoRow>
              <BrandIcon name="adresse" size={20} />
              <span>
                {t('course.appointment.address', { address: course.position })}
              </span>
            </InfoRow>
          )}
        </Body>
      </Card>
    </Box>
  );
};

FinishedAppointmentCard.propTypes = {
  course: PropTypes.shape({
    date: PropTypes.string,
    position: PropTypes.string,
    fixer: PropTypes.shape({
      prenom: PropTypes.string,
      photo_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
  }),
};

FinishedAppointmentCard.defaultProps = {
  course: null,
};

export default FinishedAppointmentCard;
